/**
 * Envio ao Full (Mercado Livre) — parâmetros e tags do cálculo de necessidade.
 *
 * O cálculo em si mora em `pipeline/derive/FullShipment.js`. Aqui ficam só as
 * constantes: defaults, normalização do bloco `preset.full` (payload JSON do
 * preset, snake_case) e os nomes das tags que o Selector devolve em
 * `row.fullTags` (coluna "Análise Full", separada das tags gerais).
 *
 * Regra de negócio (dono, 2026-09-08):
 *   - Estoque "do Full" = ML TOTAL (disponível + não disponível). Não separa
 *     em trânsito/danificado — o dono pediu o total.
 *   - Demanda/dia = média dos últimos `avg_window_days` (7) a partir do anchor.
 *   - Alvo = demanda/dia × (min_days + lead_days) → 10 + 4 = 14 dias.
 *   - Necessidade = alvo − ML total. Sugestão = min(necessidade, Bling).
 *   - Cobertura Full (dias) = ML total / demanda-dia:
 *        < urgent_days (10)    → 'full urgente'
 *        < attention_days (20) → 'full atencao'
 *        acima                 → nada
 *   - Full zerado + Bling > 0 + vendeu nos últimos `history_days` (30)
 *        → 'full sugestao' (candidato a entrar/voltar pro Full)
 *   - Necessidade > Bling → 'bling insuficiente' (a sugestão fica capada).
 */

/** Chaves persistidas em `preset.full` (snake_case, igual `stock_rules`). */
export const DEFAULT_FULL_PARAMS = Object.freeze({
    avg_window_days: 7,
    min_days: 10,
    lead_days: 4,
    urgent_days: 10,
    attention_days: 20,
    history_days: 30,
});

/**
 * Descrição de cada parâmetro — usada pelo PresetBuilder (label + hint) e
 * pra manter a UI e a normalização com a MESMA lista de chaves.
 */
export const FULL_PARAM_DEFS = [
    { key: 'avg_window_days', label: 'Janela da média', unit: 'dias', min: 1, max: 90, hint: 'Média diária de venda' },
    { key: 'min_days', label: 'Estoque mínimo', unit: 'dias', min: 0, max: 365, hint: 'Dias de venda no Full' },
    { key: 'lead_days', label: 'Lead time', unit: 'dias', min: 0, max: 90, hint: 'Envio até ficar disponível' },
    { key: 'urgent_days', label: 'Urgente abaixo de', unit: 'dias', min: 0, max: 365, hint: 'Cobertura Full' },
    { key: 'attention_days', label: 'Atenção abaixo de', unit: 'dias', min: 0, max: 365, hint: 'Cobertura Full' },
    {
        key: 'history_days',
        label: 'Histórico de venda',
        unit: 'dias',
        min: 1,
        max: 365,
        hint: 'Pra sugerir Full zerado',
    },
];

function clampInt(value, def, min, max) {
    const n = Number(value);
    if (!Number.isFinite(n)) {
        return def;
    }
    return Math.min(max, Math.max(min, Math.round(n)));
}

/**
 * Normaliza `preset.full` (parcial, ausente ou inválido) num objeto camelCase
 * completo pro derive consumir. Sempre devolve TODAS as chaves — o derive
 * nunca precisa de fallback.
 *
 * @param {Object|null|undefined} raw  bloco `full` do payload do preset
 * @returns {{avgWindowDays: number, minDays: number, leadDays: number, urgentDays: number, attentionDays: number, historyDays: number}}
 */
export function NormalizeFullParams(raw) {
    const src = raw && typeof raw === 'object' ? raw : {};
    const byKey = Object.fromEntries(FULL_PARAM_DEFS.map((d) => [d.key, d]));
    const read = (key) => clampInt(src[key], DEFAULT_FULL_PARAMS[key], byKey[key].min, byKey[key].max);
    return {
        avgWindowDays: read('avg_window_days'),
        minDays: read('min_days'),
        leadDays: read('lead_days'),
        urgentDays: read('urgent_days'),
        attentionDays: read('attention_days'),
        historyDays: read('history_days'),
    };
}

// System tags do Full — lowercase sem acento, igual às demais system tags
// ('reputacao warning' etc). Filtro de tags compara lowercase literal.
export const FULL_TAG_URGENT = 'full urgente';
export const FULL_TAG_ATTENTION = 'full atencao';
export const FULL_TAG_SUGGEST = 'full sugestao';
export const FULL_TAG_BLING_SHORT = 'bling insuficiente';

export const FULL_TAGS = Object.freeze([FULL_TAG_URGENT, FULL_TAG_ATTENTION, FULL_TAG_SUGGEST, FULL_TAG_BLING_SHORT]);

const FULL_TAG_SET = new Set(FULL_TAGS);

export function IsFullTag(tag) {
    return FULL_TAG_SET.has(String(tag || '').toLowerCase());
}
